/**
 * Shared value-mapping for the per-rule "counter-hedge pricing mode" Select.
 *
 * The rule field is `counterHedgeMode?: HedgeMode | null`, where `null`
 * (or absent) means "inherit the global default". MUI `<Select>` can't carry
 * a real `null` as a value cleanly, so the Select operates over a string value
 * space where the sentinel `INHERIT_SENTINEL` stands in for `null`.
 *
 * - `toSelectValue(mode)` maps the rule field → the Select's `value` prop.
 * - `fromSelectValue(value)` maps the Select's chosen value → the DTO field
 *   (`null` for inherit, otherwise the concrete mode).
 */

export type HedgeMode =
  | 'always-threshold'
  | 'gtc-to-max-sum'
  | 'market-to-hedge';

export const INHERIT_SENTINEL = '__inherit__';

export type HedgeModeSelectValue = HedgeMode | typeof INHERIT_SENTINEL;

export const HEDGE_MODE_OPTIONS: {
  value: HedgeModeSelectValue;
  /** Full label shown in the open dropdown menu. */
  label: string;
  /** Compact label shown in the closed (collapsed) per-rule Select. */
  short: string;
}[] = [
  { value: INHERIT_SENTINEL, label: 'Inherit (global)', short: 'Glo' },
  { value: 'always-threshold', label: 'Always threshold', short: 'Thr' },
  { value: 'gtc-to-max-sum', label: 'GTC to max sum', short: 'Max' },
  { value: 'market-to-hedge', label: 'Market hedge', short: 'Mkt' }
];

/** Compact label for a Select value — used by the collapsed row selector. */
export function shortLabelFor(value: HedgeModeSelectValue): string {
  return HEDGE_MODE_OPTIONS.find(o => o.value === value)?.short ?? 'Glo';
}

/** The 3 concrete modes (no inherit) — used by the global-default Select. */
export const HEDGE_MODE_GLOBAL_OPTIONS: {
  value: HedgeMode;
  label: string;
  short: string;
}[] = HEDGE_MODE_OPTIONS.filter(
  (o): o is { value: HedgeMode; label: string; short: string } =>
    o.value !== INHERIT_SENTINEL
);

/** Rule field (`HedgeMode | null | undefined`) → Select value. */
export function toSelectValue(
  mode: HedgeMode | null | undefined
): HedgeModeSelectValue {
  return mode ?? INHERIT_SENTINEL;
}

/** Select value → DTO field (`null` for inherit). */
export function fromSelectValue(
  value: HedgeModeSelectValue
): HedgeMode | null {
  return value === INHERIT_SENTINEL ? null : value;
}

/**
 * Map a row's pre-rule "pending" selections into the create-payload fields.
 * `makerSizeMode` is always emitted; `counterHedgeMode` is ALWAYS emitted too —
 * an explicit `null` for inherit. The explicit null defends against backends
 * predating the null schema default, where omitting the field let the old
 * non-null default pin the new rule instead of inheriting.
 * `counterStuckMarketHedgeSeconds` follows the same always-emitted contract —
 * explicit `null` for off (opener: inherit the group fallback).
 * Mirrors the opener.
 */
export function buildPendingModeFields(
  pendingSizeMode: 'dynamic' | 'fixed',
  pendingHedgeMode: HedgeModeSelectValue,
  pendingStuckSeconds: number | null
): {
  makerSizeMode: 'dynamic' | 'fixed';
  counterHedgeMode: HedgeMode | null;
  counterStuckMarketHedgeSeconds: number | null;
} {
  return {
    makerSizeMode: pendingSizeMode,
    counterHedgeMode: fromSelectValue(pendingHedgeMode),
    counterStuckMarketHedgeSeconds: pendingStuckSeconds
  };
}

/** The live global hedge config the option labels resolve against. */
export interface GlobalHedgeConfig {
  /** Global `counterHedgeModeDefault` (system_config). */
  mode?: HedgeMode;
  /** Global `marketHedgeMaxSum` (system_config) — the gtc-to-max-sum cap. */
  maxSum?: number;
}

/**
 * Compact "<short> (<numeric target>)" summary of a concrete mode. Targets
 * mirror the Rust engine's `counter_limit_for_mode`:
 *  - `always-threshold` rests at the rule-threshold-implied price.
 *  - `gtc-to-max-sum` rests at the price making the combined sum equal the
 *    global `marketHedgeMaxSum` cap.
 *  - `market-to-hedge` crosses the book at market price ALWAYS — no cap.
 */
function concreteModeSummary(mode: HedgeMode, maxSum?: number): string {
  switch (mode) {
    case 'always-threshold':
      return 'Thr (threshold)';
    case 'gtc-to-max-sum':
      return maxSum != null ? `Max (cap ${maxSum})` : 'Max (max sum)';
    case 'market-to-hedge':
      return 'Mkt';
  }
}

/**
 * Full dropdown label for a per-rule Select option, resolved against the
 * live global config so "Inherit" is never ambiguous: it always names the
 * concrete mode + numeric target it currently resolves to.
 */
export function formatHedgeModeOptionLabel(
  value: HedgeModeSelectValue,
  global?: GlobalHedgeConfig
): string {
  switch (value) {
    case INHERIT_SENTINEL:
      return global?.mode
        ? `Inherit — ${concreteModeSummary(global.mode, global.maxSum)}`
        : 'Inherit (global)';
    case 'always-threshold':
      return 'Always threshold';
    case 'gtc-to-max-sum':
      // Engine rests the GTC at the marketHedgeMaxSum ceiling.
      return global?.maxSum != null
        ? `GTC to max sum (cap ${global.maxSum})`
        : 'GTC to max sum';
    case 'market-to-hedge':
      // Crosses at market price always — no cap, so no numeric target to show.
      return 'Market hedge';
  }
}

/**
 * The 4 per-rule options with labels resolved against the live global
 * config (see `formatHedgeModeOptionLabel`). Values/order/short labels are
 * identical to `HEDGE_MODE_OPTIONS`.
 */
export function hedgeModeOptions(global?: GlobalHedgeConfig): {
  value: HedgeModeSelectValue;
  label: string;
  short: string;
}[] {
  return HEDGE_MODE_OPTIONS.map(o => ({
    ...o,
    label: formatHedgeModeOptionLabel(o.value, global)
  }));
}
